/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Resolução Canônica e Determinística de arenaId para o Módulo de Vendas e Inventário.
 * 
 * Regras Obrigatórias:
 * 1. Chave primária da Arena: arenaId.
 * 2. PROIBIDO utilizar nome da Arena, email, nome do proprietário ou localStorage compartilhado como chave.
 * 3. PROIBIDO qualquer reaproveitamento ou herança de produtos/vendas/estoque entre Arenas.
 * 4. Nova Arena sempre inicia com produtos = 0, estoque = 0, faturamento = 0.
 */

import { auth } from "../../utils/firebase";
import { UserProfile, CourtItem } from "../../types/userProfile";

export interface ResolveArenaIdOptions {
  arenaId?: string | null;
  userProfile?: Partial<UserProfile> | null;
  courts?: Partial<CourtItem>[] | null;
  ownerId?: string | null;
}

/**
 * Resolve o arenaId canônico de forma determinística a partir da
 * identidade autenticada e da relação oficial Owner → Arena.
 */
export function resolveCanonicalArenaId(options?: ResolveArenaIdOptions | string | null): string {
  if (!options) {
    const uid = auth?.currentUser?.uid || "";
    return uid ? `arena_${uid.trim()}` : "";
  }

  if (typeof options === "string") {
    const clean = options.trim();
    if (
      clean &&
      clean !== "arena_padrao" &&
      clean !== "default_owner" &&
      clean !== "owner" &&
      clean !== "null" &&
      clean !== "undefined"
    ) {
      if (clean.startsWith("arena_")) {
        return clean;
      }
      return `arena_${clean}`;
    }
    const uid = auth?.currentUser?.uid || "";
    return uid ? `arena_${uid.trim()}` : "";
  }

  const ownerUid = (
    options.ownerId ||
    options.userProfile?.id ||
    auth?.currentUser?.uid ||
    ""
  ).trim();

  // 1. arenaId explícito fornecido válido (e estritamente diferente de ownerUid)
  if (
    options.arenaId &&
    typeof options.arenaId === "string" &&
    options.arenaId.trim() !== "" &&
    options.arenaId !== "arena_padrao" &&
    options.arenaId !== "default_owner" &&
    options.arenaId !== "owner" &&
    options.arenaId !== "null" &&
    options.arenaId !== "undefined" &&
    (!ownerUid || options.arenaId.trim() !== ownerUid)
  ) {
    return options.arenaId.trim();
  }

  // 2. userProfile.arenaId (estritamente diferente de ownerUid)
  if (
    options.userProfile?.arenaId &&
    typeof options.userProfile.arenaId === "string" &&
    options.userProfile.arenaId.trim() !== "" &&
    options.userProfile.arenaId !== "arena_padrao" &&
    options.userProfile.arenaId !== "null" &&
    (!ownerUid || options.userProfile.arenaId.trim() !== ownerUid)
  ) {
    return options.userProfile.arenaId.trim();
  }

  // 3. Quadras associadas ao proprietário com arenaId
  const uid = ownerUid;

  if (options.courts && Array.isArray(options.courts) && options.courts.length > 0) {
    const matchedCourt = options.courts.find((c) => {
      if (!c.arenaId || c.arenaId === "arena_padrao" || c.arenaId === "null" || (uid && c.arenaId === uid)) return false;
      if (!uid) return true;
      return (
        c.ownerId === uid ||
        c.userId === uid ||
        (c as any).arenaOwnerId === uid ||
        (c as any).ownerUid === uid
      );
    });
    if (matchedCourt?.arenaId) {
      return matchedCourt.arenaId.trim();
    }
  }

  // 4. Determinístico a partir da identidade oficial autenticada (1:1 estável)
  if (uid && !uid.startsWith("guest") && uid !== "default_owner" && uid !== "owner") {
    // Se o uid já possui formato de arenaId (ex: "arena_xyz"), preserva diretamente
    if (uid.startsWith("arena_")) {
      return uid;
    }
    return `arena_${uid}`;
  }

  return "";
}
